import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import VideoIcon from '@mui/icons-material/OndemandVideoOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import styled from '@mui/material/styles/styled';
import LogoIcon from 'assets/svg/logo-icon.svg';
import Dropzone from 'components/form/Dropzone';
import TextInput from 'components/form/TextInput';
import { BUCKET_URL } from 'constants/index';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getMediaUrl, uploadMedia } from 'services/media';

const Option = styled(Box)`
  flex: 1;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  cursor: pointer;
  border: 1px dashed #a4a4a4;
  justify-content: center;
  background-color: ${({ selected, theme }) =>
    selected ? theme.palette.primary.main : 'transparent'};
  color: ${({ selected, theme }) =>
    selected ? theme.palette.primary.contrastText : theme.palette.text.primary};
  pointer-events: ${({ selected }) => (selected ? 'none' : 'auto')};
  &:hover {
    background-color: ${({ theme }) => theme.palette.grey[200]};
  }
  svg {
    margin-right: 8px;
  }
  p {
    color: inherit;
  }
`;

const LogoVideoOption = ({
  logo_or_video,
  logo_or_video_url,
  onInputChange,
}) => {
  const account = useSelector((s) => s.settings.account);
  const [t] = useTranslation('connectCard');

  const onLogoSelect = async (value) => {
    if (value && value.length) {
      const file = value[0];
      const { location } = await uploadMedia(file, account.account_id);
      onInputChange({
        target: { name: 'logo_or_video_url', value: BUCKET_URL + location },
      });
    } else {
      onInputChange({
        target: { name: 'logo_or_video_url', value: null },
      });
    }
  };

  const onTypeSelect = (v) => {
    onInputChange({
      target: { name: 'logo_or_video', value: v },
    });
    onInputChange({
      target: { name: 'logo_or_video_url', value: null },
    });
  };

  return (
    <Box>
      <Typography paragraph fontWeight={500}>
        {t('cc-form-select-logo', 'Add Logo or Video')}
      </Typography>
      <Stack direction="row" justifyContent="space-between" spacing={4} mb={2}>
        <Option
          selected={logo_or_video === 'logo'}
          onClick={() => onTypeSelect('logo')}
        >
          <LogoIcon />
          <Typography>{t('logo', 'Logo')}</Typography>
        </Option>
        <Option
          selected={logo_or_video === 'video'}
          onClick={() => onTypeSelect('video')}
        >
          <VideoIcon />
          <Typography>{t('video', 'Video')}</Typography>
        </Option>
      </Stack>
      {logo_or_video === 'logo' ? (
        <>
          {logo_or_video_url && (
            <Box mb={2} bgcolor="background.default">
              <Box height={100} display="flex" justifyContent="center" pt={1}>
                <img
                  src={logo_or_video_url}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              </Box>
              <Box mt={1} mb={1} px={4}>
                <Button
                  fullWidth
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={(e) => onLogoSelect()}
                >
                  <DeleteIcon />
                </Button>
              </Box>
            </Box>
          )}
          <Box
            sx={{
              '& .dropzone': {
                padding: 0,
              },
            }}
          >
            <Dropzone
              className="dropzone"
              showPreview={false}
              onChange={onLogoSelect}
              placeholder={
                <Button fullWidth startIcon={<CloudUploadOutlinedIcon />}>
                  {t(
                    'select-custom-logo-placeholder',
                    'Select a custom logo (optional)',
                  )}
                </Button>
              }
            />
          </Box>
        </>
      ) : (
        logo_or_video === 'video' && (
          <Box mb={2}>
            <TextInput
              fullWidth
              margin="none"
              value={logo_or_video_url}
              name="logo_or_video_url"
              onChange={onInputChange}
              placeholder={t('url-input-placeholder', 'Vimeo or Youtube URL')}
            />
          </Box>
        )
      )}
    </Box>
  );
};

export default LogoVideoOption;
